"use client"

/**
 * Quit confirmation for the listing-creation flow — shown when the seller
 * closes the flow (X / Escape / backdrop / back at step 0) with a meaningful,
 * dirty form. Three outcomes: keep editing (Cancel — also what Escape and
 * backdrop resolve to, the safest action), discard the session's changes
 * (destructive), or save as a draft (primary, async).
 *
 * Canon: AlertDialog (DIALOGS.md §4 — blocking confirm, both viewports, no
 * DockSheet branch). Structure mirrors delete-confirmation-dialog.tsx;
 * async-save states mirror cancel-private-listing-dialog.tsx (Loader2 +
 * inline error + disabled buttons); the primary action keeps the dialog open
 * during the async call via e.preventDefault() (offer-decline pattern); the
 * 3-button footer layout override is the CurrencyConversionDialog precedent —
 * DOM order [Cancel, Discard, Save] renders Cancel · Discard · Save on
 * desktop and stacks Save on top on mobile via flex-col-reverse.
 */

import { Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export type QuitFlowState =
  | { kind: "idle" }
  | { kind: "confirming" }
  | { kind: "saving" }
  | { kind: "saveError"; message: string }

interface QuitCreationDialogProps {
  state: QuitFlowState
  /** Keep editing — the flow stays open. */
  onCancelAction: () => void
  /** Discard this session's changes and close (never deletes a saved draft row). */
  onDiscardAction: () => void
  /** Upload photos + persist the draft, then close. */
  onSaveDraftAction: () => void
}

export function QuitCreationDialog({
  state,
  onCancelAction,
  onDiscardAction,
  onSaveDraftAction,
}: QuitCreationDialogProps) {
  const t = useTranslations("listingCreation.quitConfirm")
  const tCommon = useTranslations("common")
  const saving = state.kind === "saving"

  return (
    <AlertDialog
      open={state.kind !== "idle"}
      onOpenChange={(open) => {
        // Escape/backdrop resolve to "keep editing"; ignored mid-save — the
        // controlled `open` keeps the dialog up until the save settles.
        if (!open && !saving) onCancelAction()
      }}
    >
      <AlertDialogContent className="w-[calc(100vw-2rem)] sm:w-full">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("title")}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <AlertDialogDescription>
            <span className="block">{t("description")}</span>
            {state.kind === "saveError" ? (
              <span className="block mt-2 text-destructive font-medium">
                {state.message}
              </span>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogBody>
        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <AlertDialogCancel disabled={saving} className="mt-0">
            {tCommon("cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={saving}
            onClick={onDiscardAction}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t("discard")}
          </AlertDialogAction>
          <AlertDialogAction
            disabled={saving}
            onClick={(e) => {
              e.preventDefault()
              onSaveDraftAction()
            }}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t("saving")}
              </>
            ) : (
              t("saveDraft")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
